import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter } from '@angular/material/core';
import { FormlyErrorComponent } from '../formly-error.component';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { FieldLabelRowComponent } from './field-label-row.component';
import { ClearableFieldType } from './clearable-field.type';
import { DateTime } from 'luxon';
import { commitUserValue } from './user-edit.util';

@Component({
  selector: 'formly-field-date-week',
  template: `
    <mat-form-field class="w-full" [class.mat-form-field-invalid]="formControl.invalid && formControl.touched">
      <mat-label>
        <app-field-label-row
          [label]="props.label + (field.props.required ? ' *' : '')"
          [required]="field.props.required"
          [info]="props.info"></app-field-label-row>
      </mat-label>
      <!-- The visible input is a display mirror of the hidden Material control, so it carries
           neither matInput nor MatDatepickerInput. Keep its explicit click and ALT+ARROW_DOWN
           routes alongside the toggle restored by the focused state. -->
      <!-- [disabled] as well as readonly: disabling the field disables the hidden control, but
           the mirror kept its own click/ALT+ARROW_DOWN routes, so a disabled week field still
           opened its picker and stayed focusable (caught by the disabled invariant on the new
           Velden/Datum en tijd/Weeknummer story). -->
      <input
        #weekInput
        class="dd-picker-input"
        [value]="displayValue"
        [attr.aria-label]="props.label"
        [attr.aria-invalid]="formControl.invalid && formControl.touched"
        [disabled]="isDisabled()"
        readonly
        (click)="picker.open()"
        (keydown.alt.arrowdown)="picker.open()" />
      <!-- Not bound to formControl: the stored week token ('2027-W8') is no date the adapter can
           parse, so a bound datepicker flags matDatepickerParse and invalidates the form. It gets
           the week's Monday instead and writes the token back through onDateChange. -->
      <input
        class="hidden dd-picker-input"
        matInput
        [formlyAttributes]="field"
        [matDatepicker]="picker"
        [value]="pickerDate"
        [disabled]="isDisabled()"
        (dateChange)="onDateChange($event)"
        readonly
        (click)="picker.open()" />
      <span class="dd-clear-suffix dd-addon-suffix" matSuffix>
        <mat-datepicker-toggle class="dd-addon-suffix__passive" [for]="picker"></mat-datepicker-toggle>
        <app-clear-field-button
          [field]="field"
          [hasValue]="hasClearableValue()"
          [disabled]="isDisabled()"
          [ariaLabel]="clearAriaLabel()"
          [focusTarget]="weekInput"
          (clearSelection)="clear()" />
      </span>
      <mat-datepicker #picker></mat-datepicker>
      @if (props.description) {
        <mat-hint>{{ props.description }}</mat-hint>
      }
    </mat-form-field>
    <app-formly-error [field]="field"></app-formly-error>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatInputModule,
    MatDatepickerModule,
    FormsModule,
    FormlyErrorComponent,
    ClearFieldButtonComponent,
    FieldLabelRowComponent,
  ],
})
export class FormlyFieldDateWeekComponent extends ClearableFieldType<FieldTypeConfig> implements OnInit {
  readonly #dateAdapter = inject(DateAdapter);
  displayValue: string = '';
  pickerDate: unknown = null;

  ngOnInit() {
    const value = this.formControl.value;
    if (typeof value === 'string' && value) {
      this.updateDisplayFromWeekString(value);
    }
  }

  onDateChange(event: any) {
    if (event.value) {
      this.updateWeekValue(event.value);
    }
  }

  // Deviates from the base: the "Week N, YYYY" text is rendered from a derived field, not from the
  // control value, so clearing has to reset that projection as well.
  override clear(): void {
    super.clear();
    this.displayValue = '';
    this.pickerDate = null;
  }

  private updateWeekValue(date: unknown) {
    const adapter = this.#dateAdapter;
    const selected = DateTime.local(adapter.getYear(date), adapter.getMonth(date) + 1, adapter.getDate(date));
    // Unpadded ('2027-W8'): the Flutter app writes and filters entry.date.week in this exact form.
    // The ISO week-year is the year of the week's Thursday: 29-12-2025 is 2026-W1, 01-01-2027 is 2026-W53.
    const weekString = `${selected.weekYear}-W${selected.weekNumber}`;

    if (weekString !== this.formControl.value) {
      // The hidden input is not a CVA for this control, so marking the user edit is on us (#21026).
      commitUserValue(this.formControl, weekString);
    }
    this.updateDisplayFromWeekString(weekString);
  }

  // Reads both '2027-W8' and the zero-padded '2027-W08' that older Portal versions stored.
  private updateDisplayFromWeekString(weekString: string) {
    const [year, week] = weekString.split('-W').map(part => parseInt(part, 10));
    this.displayValue = `Week ${week}, ${year}`;
    const monday = DateTime.fromObject({ weekYear: year, weekNumber: week, weekday: 1 });
    this.pickerDate = monday.isValid ? this.#dateAdapter.createDate(monday.year, monday.month - 1, monday.day) : null;
  }
}
